import type {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import axios from "axios";
import { getToken } from "./JwtService";

/** PatientPortalApi (App Service patient-portal-api). */
const defaultApiUrl = "https://patient-portal-api-aneqcyf5hkdqejgr.southcentralus-01.azurewebsites.net";

/** In dev, use /api so Vite proxy forwards to the API and avoids CORS. */
export function getApiBaseUrl(): string {
  if (import.meta.env.DEV) {
    return "/api";
  }
  return import.meta.env.VITE_APP_API_URL ?? defaultApiUrl;
}

const baseURL = getApiBaseUrl();

let axiosInstance: AxiosInstance;

function setInterceptors(instance: AxiosInstance): void {
  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const token = getToken();
      if (token && config.headers) {
        config.headers.Authorization = `bearer ${token}`;
      }
      return config;
    },
    (error: AxiosError) => {
      console.error("Request Error:", error);
      return Promise.reject(error);
    }
  );

  instance.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error: AxiosError) => {
      if (error.response) {
        if (error.response.status === 401) {
          sessionStorage.clear();
          // Flag the expiry so the login page can explain why they landed there
          // (sessionStorage was just cleared, so pass it via the URL instead).
          window.location.href = "/account/login?sessionExpired=1";
        } else {
          console.error(
            `Response Error [${error.response.status}]:`,
            error.response.data
          );
        }
      } else if (error.request) {
        console.error("No Response Received:", error.request);
      } else {
        console.error("Error in setup:", error.message);
      }
      return Promise.reject(error);
    }
  );
}

export function init(): void {
  axiosInstance = axios.create({
    baseURL,
    // Fail a hung request instead of leaving it pending forever — a stalled save must surface as
    // an error the patient can see and retry, never appear saved when it isn't.
    timeout: 30000,
    headers: { "Content-Type": "application/json" },
  });
  setInterceptors(axiosInstance);
}

export function getClient(): AxiosInstance {
  if (!axiosInstance) {
    init();
  }
  return axiosInstance;
}

export function setHeader(): void {
  const client = getClient();
  const token = getToken();
  if (client.defaults.headers && token) {
    (client.defaults.headers.common as Record<string, string>).Authorization = `bearer ${token}`;
    (client.defaults.headers.common as Record<string, string>)["Accept"] =
      "application/json";
  }
}

export function query(resource: string, params: Record<string, unknown>): Promise<AxiosResponse> {
  setHeader();
  return getClient().get(resource, params);
}

export function get(resource: string): Promise<AxiosResponse> {
  setHeader();
  return getClient().get(resource);
}

export function getWithManualToken(resource: string, token: string): Promise<AxiosResponse> {
  const client = getClient();
  (client.defaults.headers.common as Record<string, string>).Authorization = `bearer ${token}`;
  return client.get(resource);
}

export function post(
  resource: string,
  params: unknown,
  config?: { headers?: Record<string, string> }
): Promise<AxiosResponse> {
  setHeader();
  return getClient().post(resource, params, config);
}

export function postWithManualToken(
  resource: string,
  params: unknown,
  bearerToken: string
): Promise<AxiosResponse> {
  const client = getClient();
  (client.defaults.headers.common as Record<string, string>).Authorization = `bearer ${bearerToken}`;
  return client.post(resource, params);
}

export function update(
  resource: string,
  slug: string,
  params: unknown
): Promise<AxiosResponse> {
  return getClient().put(`${resource}/${slug}`, params);
}

export function put(resource: string, params: unknown): Promise<AxiosResponse> {
  return getClient().put(resource, params);
}

export function del(resource: string): Promise<AxiosResponse> {
  return getClient().delete(resource);
}

export default {
  init,
  getClient,
  setHeader,
  query,
  get,
  getWithManualToken,
  post,
  postWithManualToken,
  update,
  put,
  delete: del,
};
